interface HDHLogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  variant?: 'full' | 'icon' | 'text'
  className?: string
}

export default function HDHLogo({ 
  size = 'md', 
  variant = 'full',
  className = '' 
}: HDHLogoProps) {
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  }

  const textSizeClasses = {
    sm: 'text-lg',
    md: 'text-2xl',
    lg: 'text-4xl',
    xl: 'text-6xl'
  }

  if (variant === 'text') {
    return (
      <span className={`font-heading font-bold bg-gradient-to-r from-gold-400 via-gold-500 to-violet-400 bg-clip-text text-transparent ${textSizeClasses[size]} ${className}`} 
            style={{
              fontFamily: 'Georgia, serif',
              textShadow: `
                1px 1px 0px rgba(0, 0, 0, 0.4),
                -1px -1px 0px rgba(0, 0, 0, 0.4),
                1px -1px 0px rgba(0, 0, 0, 0.4),
                -1px 1px 0px rgba(0, 0, 0, 0.4),
                0px 0px 4px rgba(0, 0, 0, 0.3)
              `
            }}>
        HDH
      </span>
    )
  }

  if (variant === 'icon') {
    return (
      <div className={`${sizeClasses[size]} ${className}`}>
        <svg
          viewBox="0 0 64 64"
          className="w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Outer tech ring */}
          <circle
            cx="32"
            cy="32"
            r="28"
            fill="none"
            stroke="url(#techRingGradient)"
            strokeWidth="2"
            strokeDasharray="4 4"
            opacity="0.8"
          />
          
          {/* Inner hexagon for tech feel */}
          <path
            d="M32 12 L44 20 L44 44 L32 52 L20 44 L20 20 Z"
            fill="url(#techGradient)"
            stroke="url(#goldPurpleBorder)"
            strokeWidth="2"
          />
          
          {/* HDH letters with tech styling */}
          <g fill="url(#letterTechGradient)" fontFamily="monospace" fontSize="12" fontWeight="bold">
            <text x="24" y="32" textAnchor="middle" dominantBaseline="central" stroke="url(#letterTechGradient)" strokeWidth="0.5" fill="none">
              HDH
            </text>
          </g>
          
          {/* Tech nodes */}
          <circle cx="32" cy="12" r="2" fill="#D4AF37" opacity="0.8" />
          <circle cx="44" cy="32" r="2" fill="#6A0DAD" opacity="0.8" />
          <circle cx="32" cy="52" r="2" fill="#D4AF37" opacity="0.8" />
          <circle cx="20" cy="32" r="2" fill="#6A0DAD" opacity="0.8" />
          
          {/* Glow effect */}
          <circle
            cx="32"
            cy="32"
            r="30"
            fill="none"
            stroke="url(#techGlow)"
            strokeWidth="1"
            opacity="0.3"
          />
          
          {/* Gradients */}
          <defs>
            <linearGradient id="techGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#6A0DAD" stopOpacity="0.9" />
            </linearGradient>
            
            <linearGradient id="techRingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#D4AF37" />
              <stop offset="100%" stopColor="#6A0DAD" />
            </linearGradient>
            
            <linearGradient id="goldPurpleBorder" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#D4AF37" />
              <stop offset="100%" stopColor="#6A0DAD" />
            </linearGradient>
            
            <linearGradient id="letterTechGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FFFFFF" />
              <stop offset="100%" stopColor="#F8D86F" />
            </linearGradient>
            
            <radialGradient id="techGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#6A0DAD" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#6A0DAD" stopOpacity="0" />
            </radialGradient>
          </defs>
        </svg>
      </div>
    )
  }

  // Full variant (icon + text)
  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <div className={sizeClasses[size]}>
        <svg
          viewBox="0 0 64 64"
          className="w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Outer tech ring */}
          <circle
            cx="32"
            cy="32"
            r="28"
            fill="none"
            stroke="url(#techRingGradient)"
            strokeWidth="2"
            strokeDasharray="4 4"
            opacity="0.8"
          />
          
          {/* Inner hexagon for tech feel */}
          <path
            d="M32 12 L44 20 L44 44 L32 52 L20 44 L20 20 Z"
            fill="url(#techGradient)"
            stroke="url(#goldPurpleBorder)"
            strokeWidth="2"
          />
          
          {/* HDH letters with tech styling */}
          <g fill="url(#letterTechGradient)" fontFamily="monospace" fontSize="12" fontWeight="bold">
            <text x="24" y="32" textAnchor="middle" dominantBaseline="central" stroke="url(#letterTechGradient)" strokeWidth="0.5" fill="none">
              HDH
            </text>
          </g>
          
          {/* Tech nodes */}
          <circle cx="32" cy="12" r="2" fill="#D4AF37" opacity="0.8" />
          <circle cx="44" cy="32" r="2" fill="#6A0DAD" opacity="0.8" />
          <circle cx="32" cy="52" r="2" fill="#D4AF37" opacity="0.8" />
          <circle cx="20" cy="32" r="2" fill="#6A0DAD" opacity="0.8" />
          
          {/* Glow effect */}
          <circle
            cx="32"
            cy="32"
            r="30"
            fill="none"
            stroke="url(#techGlow)"
            strokeWidth="1"
            opacity="0.3"
          />
          
          {/* Gradients */}
          <defs>
            <linearGradient id="techGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#6A0DAD" stopOpacity="0.9" />
            </linearGradient>
            
            <linearGradient id="techRingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#D4AF37" />
              <stop offset="100%" stopColor="#6A0DAD" />
            </linearGradient>
            
            <linearGradient id="goldPurpleBorder" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#D4AF37" />
              <stop offset="100%" stopColor="#6A0DAD" />
            </linearGradient>
            
            <linearGradient id="letterTechGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FFFFFF" />
              <stop offset="100%" stopColor="#F8D86F" />
            </linearGradient>
            
            <radialGradient id="techGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#6A0DAD" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#6A0DAD" stopOpacity="0" />
            </radialGradient>
          </defs>
        </svg>
      </div>
      <span className={`font-heading font-bold bg-gradient-to-r from-gold-400 via-gold-500 to-violet-400 bg-clip-text text-transparent ${textSizeClasses[size]}`}
            style={{
              fontFamily: 'Georgia, serif',
              textShadow: `
                1px 1px 0px rgba(0, 0, 0, 0.4),
                -1px -1px 0px rgba(0, 0, 0, 0.4),
                1px -1px 0px rgba(0, 0, 0, 0.4),
                -1px 1px 0px rgba(0, 0, 0, 0.4),
                0px 0px 4px rgba(0, 0, 0, 0.3)
              `
            }}>
        HarmonyDataHub
      </span>
    </div>
  )
}
